import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, KeyRound, Server, Film, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';

type NzbCfg = { host: string; username: string; password_set: boolean; category: string };
type EmbyCfg = { url: string; api_key_set: boolean };

/**
 * Einstellungen.
 *
 * Grundregel dieser Seite: **Geheimnisse werden nie angezeigt.** Das
 * Backend gibt sie auch gar nicht heraus — es meldet nur `password_set`,
 * `api_key_set`, `configured`. Die Felder bleiben deshalb leer und
 * ueberschreiben nur dann etwas, wenn wirklich getippt wurde. Ein leeres
 * Feld heisst "unveraendert lassen", nicht "loeschen".
 */
export function Settings() {
  return (
    <>
      <div className="kopf">
        <div>
          <h1>Einstellungen</h1>
          <p>Zugänge zu Indexer, NZBGet und Emby. Gespeicherte Geheimnisse werden nie angezeigt.</p>
        </div>
      </div>
      <div className="stapel">
        <IndexerKarte />
        <NzbgetKarte />
        <EmbyKarte />
      </div>
    </>
  );
}

function Zustand({ gesetzt, text }: { gesetzt: boolean; text?: string }) {
  return gesetzt
    ? <span className="chip chip--gut"><CheckCircle2 size={12} /> {text ?? 'hinterlegt'}</span>
    : <span className="chip chip--warn"><XCircle size={12} /> {text ?? 'fehlt'}</span>;
}

function IndexerKarte() {
  const [konfiguriert, setKonfiguriert] = useState<boolean | null>(null);
  const [key, setKey] = useState('');
  const [meldung, setMeldung] = useState<{ art: 'gut' | 'schlecht'; text: string } | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = () => api<{ configured: boolean }>('/settings/api-key')
    .then((d) => setKonfiguriert(d.configured))
    .catch(() => setKonfiguriert(false));
  useEffect(() => { void laden(); }, []);

  async function speichern() {
    if (!key.trim()) return;
    setLaeuft(true);
    try {
      await api('/settings/api-key', { method: 'PUT', body: JSON.stringify({ api_key: key.trim() }) });
      setKey('');
      setMeldung({ art: 'gut', text: 'Schlüssel gespeichert.' });
      await laden();
    } catch (e) {
      setMeldung({ art: 'schlecht', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLaeuft(false);
    }
  }

  async function pruefen() {
    setLaeuft(true);
    setMeldung(null);
    try {
      // /settings/categories geht ueber den Indexer — antwortet er, stimmt der Schluessel.
      await api('/settings/categories');
      setMeldung({ art: 'gut', text: 'Indexer antwortet, Schlüssel ist gültig.' });
    } catch (e) {
      setMeldung({ art: 'schlecht', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="karte">
      <div className="karte__kopf">
        <span className="karte__titel"><KeyRound size={15} style={{ verticalAlign: -2, marginRight: 7 }} />Indexer</span>
        {konfiguriert !== null && <Zustand gesetzt={konfiguriert} />}
      </div>
      <div className="dialog__leib stapel">
        {meldung && <div className={`hinweis${meldung.art === 'schlecht' ? ' hinweis--schlecht' : ''}`}>{meldung.text}</div>}
        <div>
          <label className="label" htmlFor="s-key">API-Schlüssel</label>
          <input id="s-key" className="feld mono" type="password" value={key} autoComplete="off"
            placeholder={konfiguriert ? 'hinterlegt — leer lassen, um ihn zu behalten' : 'noch keiner hinterlegt'}
            onChange={(e) => setKey(e.target.value)} />
          <div className="klein schwach" style={{ marginTop: 5 }}>
            Verschlüsselt gespeichert und nie wieder ausgegeben. Ein leeres Feld ändert nichts.
          </div>
        </div>
        <div className="reihe">
          <button className="knopf knopf--haupt" onClick={speichern} disabled={laeuft || !key.trim()}>Speichern</button>
          <button className="knopf" onClick={pruefen} disabled={laeuft}>Verbindung prüfen</button>
        </div>
      </div>
    </div>
  );
}

function NzbgetKarte() {
  const [cfg, setCfg] = useState<NzbCfg | null>(null);
  const [f, setF] = useState({ host: '', username: '', password: '', category: '' });
  const [meldung, setMeldung] = useState<{ art: 'gut' | 'schlecht'; text: string } | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = () => api<NzbCfg>('/settings/nzbget').then((d) => {
    setCfg(d);
    setF({ host: d.host, username: d.username, password: '', category: d.category });
  }).catch(() => setCfg(null));
  useEffect(() => { void laden(); }, []);

  async function speichern() {
    setLaeuft(true);
    setMeldung(null);
    try {
      await api('/settings/nzbget', { method: 'PUT', body: JSON.stringify(f) });
      setF((a) => ({ ...a, password: '' }));
      setMeldung({ art: 'gut', text: 'Gespeichert.' });
      await laden();
    } catch (e) {
      setMeldung({ art: 'schlecht', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLaeuft(false);
    }
  }

  async function pruefen() {
    setLaeuft(true);
    setMeldung(null);
    try {
      // POST, nicht GET — ohne die Methode antwortet die Route mit 405.
      const d = await api<Record<string, unknown>>('/nzbget/test', { method: 'POST' });
      setMeldung({ art: 'gut', text: typeof d?.detail === 'string' ? d.detail : 'NZBGet antwortet.' });
    } catch (e) {
      setMeldung({ art: 'schlecht', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="karte">
      <div className="karte__kopf">
        <span className="karte__titel"><Server size={15} style={{ verticalAlign: -2, marginRight: 7 }} />NZBGet</span>
        {cfg && <Zustand gesetzt={cfg.password_set && !!cfg.host} text={cfg.password_set && cfg.host ? 'eingerichtet' : 'unvollständig'} />}
      </div>
      <div className="dialog__leib stapel">
        {meldung && <div className={`hinweis${meldung.art === 'schlecht' ? ' hinweis--schlecht' : ''}`}>{meldung.text}</div>}
        <div className="gitter-2">
          <div>
            <label className="label" htmlFor="n-host">Adresse</label>
            <input id="n-host" className="feld mono" value={f.host}
              onChange={(e) => setF({ ...f, host: e.target.value })} placeholder="http://127.0.0.1:6789" />
          </div>
          <div>
            <label className="label" htmlFor="n-cat">Standard-Kategorie</label>
            <input id="n-cat" className="feld" value={f.category}
              onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="z. B. Movies" />
          </div>
        </div>
        <div className="gitter-2">
          <div>
            <label className="label" htmlFor="n-user">Benutzer</label>
            <input id="n-user" className="feld" value={f.username} autoComplete="off"
              onChange={(e) => setF({ ...f, username: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="n-pw">Passwort</label>
            <input id="n-pw" className="feld" type="password" value={f.password} autoComplete="new-password"
              placeholder={cfg?.password_set ? 'hinterlegt — leer lassen, um es zu behalten' : 'noch keins hinterlegt'}
              onChange={(e) => setF({ ...f, password: e.target.value })} />
          </div>
        </div>
        <div className="reihe">
          <button className="knopf knopf--haupt" onClick={speichern} disabled={laeuft}>Speichern</button>
          <button className="knopf" onClick={pruefen} disabled={laeuft}>Verbindung prüfen</button>
        </div>
      </div>
    </div>
  );
}

function EmbyKarte() {
  const [cfg, setCfg] = useState<EmbyCfg | null>(null);
  const [f, setF] = useState({ url: '', api_key: '' });
  const [meldung, setMeldung] = useState<{ art: 'gut' | 'schlecht'; text: string } | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = () => api<EmbyCfg>('/settings/emby').then((d) => {
    setCfg(d);
    setF({ url: d.url, api_key: '' });
  }).catch(() => setCfg(null));
  useEffect(() => { void laden(); }, []);

  async function tu(pfad: string, methode: string, koerper?: unknown, erfolg = 'Erledigt.') {
    setLaeuft(true);
    setMeldung(null);
    try {
      const d = await api<Record<string, unknown>>(pfad, {
        method: methode,
        ...(koerper ? { body: JSON.stringify(koerper) } : {}),
      });
      setMeldung({ art: 'gut', text: typeof d?.detail === 'string' ? d.detail : erfolg });
      if (methode === 'PUT') { setF((a) => ({ ...a, api_key: '' })); await laden(); }
    } catch (e) {
      setMeldung({ art: 'schlecht', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="karte">
      <div className="karte__kopf">
        <span className="karte__titel"><Film size={15} style={{ verticalAlign: -2, marginRight: 7 }} />Emby</span>
        {cfg && <Zustand gesetzt={cfg.api_key_set && !!cfg.url} text={cfg.api_key_set && cfg.url ? 'eingerichtet' : 'unvollständig'} />}
      </div>
      <div className="dialog__leib stapel">
        {meldung && <div className={`hinweis${meldung.art === 'schlecht' ? ' hinweis--schlecht' : ''}`}>{meldung.text}</div>}
        <div className="gitter-2">
          <div>
            <label className="label" htmlFor="e-url">Adresse</label>
            <input id="e-url" className="feld mono" value={f.url}
              onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://stream.example.de" />
          </div>
          <div>
            <label className="label" htmlFor="e-key">API-Schlüssel</label>
            <input id="e-key" className="feld" type="password" value={f.api_key} autoComplete="off"
              placeholder={cfg?.api_key_set ? 'hinterlegt — leer lassen, um ihn zu behalten' : 'noch keiner hinterlegt'}
              onChange={(e) => setF({ ...f, api_key: e.target.value })} />
          </div>
        </div>
        <div className="reihe" style={{ flexWrap: 'wrap' }}>
          <button className="knopf knopf--haupt" disabled={laeuft}
            onClick={() => tu('/settings/emby', 'PUT', f, 'Gespeichert.')}>Speichern</button>
          <button className="knopf" disabled={laeuft}
            onClick={() => tu('/settings/emby/test', 'POST', undefined, 'Emby antwortet.')}>Verbindung prüfen</button>
          <button className="knopf" disabled={laeuft}
            onClick={() => tu('/settings/emby/refresh', 'POST', undefined, 'Bibliothek wird neu eingelesen.')}>
            <RefreshCw size={14} /> Bibliothek neu einlesen
          </button>
        </div>
        <div className="klein schwach">
          „Neu einlesen" stößt Embys Bibliotheks-Scan an — nützlich, wenn ein
          fertiger Download dort noch nicht auftaucht.
        </div>
      </div>
    </div>
  );
}
